import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardBody, CardHeader, UncontrolledTooltip, Row, Col } from 'reactstrap';
import { getCategoriasMaisAcessadas } from '../../../api/reportApi';
import TableContainer from '../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface CategoryAccessCardProps {
  campaignId: string;
}

export interface CategoryAccessCardRef {
  refresh: () => void;
}

const CategoryAccessCard = forwardRef<CategoryAccessCardRef, CategoryAccessCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const categoryData = await getCategoriasMaisAcessadas(parseInt(campaignId));
        setData(categoryData);
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Erro ao carregar dados de categorias');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={3} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="d-flex align-items-center">
              <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                <i className="ri-shield-check-line text-success fs-4"></i>
              </div>
              <div>
                <h4 className={styles.cardTitle + ' mb-0'}>Brand Safety</h4>
                <small className="text-muted">Categorias IAB por Machine Learning</small>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="text-center text-danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardBody>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="d-flex align-items-center">
              <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                <i className="ri-shield-check-line text-success fs-4"></i>
              </div>
              <div>
                <h4 className={styles.cardTitle + ' mb-0'}>Brand Safety</h4>
                <small className="text-muted">Categorias IAB por Machine Learning</small>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhuma categoria IAB identificada
            </div>
          </CardBody>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Categoria IAB',
        accessorKey: 'pageCategory',
        cell: (row: any) => {
          const category = row.getValue() === 'null' ? 'Desconhecido' : row.getValue();
          const isHighValue = ['News', 'Tecnologia', 'Negócios', 'Finance'].some(high => 
            category.toLowerCase().includes(high.toLowerCase())
          );
          const isSensitive = ['Adult', 'Violência', 'Pornografia', 'Gambling', 'Alcohol'].some(sensitive => 
            category.toLowerCase().includes(sensitive.toLowerCase())
          );
          
          let icon = 'ri-folder-line';
          let color = 'text-muted';
          
          if (isHighValue) {
            icon = 'ri-star-line';
            color = 'text-success';
          } else if (isSensitive) {
            icon = 'ri-alert-line';
            color = 'text-warning';
          }
          
          return (
            <div className="d-flex align-items-center">
              <i className={`${icon} ${color} me-2`}></i>
              <span className="fw-medium">{category}</span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Impressões',
        accessorKey: 'impression',
        cell: (row: any) => {
          const value = row.getValue() || 0;
          const total = totalImpressions;
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          
          return (
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{numeral(value).format(numeralInteger)}</span>
              <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                {percentage}%
              </span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Visíveis',
        accessorKey: 'viewable',
        cell: (row: any) => {
          const viewable = row.getValue() || 0;
          return (<span className="fw-semibold">{numeral(viewable).format(numeralInteger)}</span>);
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Cliques',
        accessorKey: 'click',
        cell: (row: any) => {
          const click = row.getValue() || 0;
          const impression = row.row.original.impression || 0;
          const ctr = impression > 0 ? ((click / impression) * 100).toFixed(2) : '0.00';
          return (
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{numeral(click).format(numeralInteger)}</span>
              <span
                className="badge bg-info bg-opacity-10 text-info px-2 py-1 ms-2"
                title="CTR = Cliques / Impressões"
              >
                {ctr}%
              </span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    // Calculate metrics
    const totalImpressions = data.reduce((sum, item) => sum + (item.impression || 0), 0);
    const topCategory = data.reduce((max, item) => 
      (item.click || 0) > (max.click || 0) ? item : max, 
      data[0] || null
    );
    
    // Count sensitive categories
    const sensitiveCategories = data.filter(item => {
      const category = item.pageCategory?.toLowerCase() || '';
      return ['adult', 'violence', 'gambling', 'alcohol', 'drugs', 'weapons', 'hate', 'terrorism'].some(sensitive => 
        category.includes(sensitive)
      );
    }).length;

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <div className="d-flex align-items-center">
            <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
              <i className="ri-shield-check-line text-success fs-4"></i>
            </div>
            <div>
              <h4 className={styles.cardTitle + ' mb-0'}>Brand Safety</h4>
              <small className="text-muted">Categorias IAB por Machine Learning</small>
            </div>
            <div className="ms-auto">
              <i 
                id="category-tooltip" 
                className="ri-question-line text-muted fs-5" 
                style={{ cursor: 'help' }}
              ></i>
              <UncontrolledTooltip placement="top" target="category-tooltip">
                Classificação automática das páginas em categorias IAB usando Machine Learning para garantir brand safety.
              </UncontrolledTooltip>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Summary Metrics */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded-3 border">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2 d-inline-flex mb-2">
                  <i className="ri-folder-line text-primary"></i>
                </div>
                <h6 className="mb-1 fw-semibold">{data.length}</h6>
                <small className="text-muted">Categorias Identificadas</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded-3 border">
                <div className={`${sensitiveCategories > 0 ? 'bg-warning' : 'bg-success'} bg-opacity-10 rounded-circle p-2 d-inline-flex mb-2`}>
                  <i className={`ri-shield-line ${sensitiveCategories > 0 ? 'text-warning' : 'text-success'}`}></i>
                </div>
                <h6 className={`mb-1 fw-semibold ${sensitiveCategories > 0 ? 'text-warning' : 'text-success'}`}>
                  {sensitiveCategories}
                </h6>
                <small className="text-muted">Categorias Sensíveis</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded-3 border">
                <div className="bg-info bg-opacity-10 rounded-circle p-2 d-inline-flex mb-2">
                  <i className="ri-trophy-line text-info"></i>
                </div>
                <h6 className="mb-1 fw-semibold text-truncate" title={topCategory?.pageCategory || 'N/A'}>
                  {topCategory.pageCategory === 'null' ? 'Desconhecido' : topCategory.pageCategory}
                </h6>
                <small className="text-muted">Categoria Principal</small>
              </div>
            </Col>
          </Row>

          {/* Table */}
          <TableContainer
            divClass="table-responsive"
            columns={columns}
            data={data}
            isGlobalFilter={false}
            customPageSize={10}
            initialSorting={[{ id: 'impression', desc: true }]}
          />
        </CardBody>
      </Card>
    );
  }
);

CategoryAccessCard.displayName = 'CategoryAccessCard';

export default CategoryAccessCard;
